import {
  AlignItems,
  AspectRatio,
  BorderStyle,
  Body1,
  Body2,
  Body3,
  Bold,
  Box,
  Button,
  ButtonColorVariant,
  Col,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Grid,
  GridDisplay,
  Heading5,
  Icon,
  IconVariant,
  Image,
  JustifyContent,
  Join,
  Link,
  Space,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import React, { useState } from 'react'

import type {
  DashboardActionRef,
  OrderPrice,
  OrderRecipe,
  OrderSectionData,
  OrderStateType,
  RecipeAction,
} from '@library/api-customer-engagement-experience'

import EmptyPlateJPG from '../../../../assets/emptyPlate.png'
import { ExpandToggle } from './ExpandToggle'
import { OrderCardPanel } from './OrderCardPanel'

// Identifies the chosen day/slot for an edit-order PATCH. `delivery_day_id` is the
// day's `id`, `delivery_slot_id` is the slot's `id`.
export type SaveSlotSelection = {
  delivery_day_id: string
  delivery_slot_id: string
  day_slot_lead_time_id: string
}

export type OrderCardProps = {
  data: OrderSectionData
  hasSaveSlotError?: boolean
  hasSkipDeliveryError?: boolean
  isSaveSlotPending?: boolean
  isSkipDeliveryPending?: boolean
  onPrimaryActionClick?: (intent: string, orderId: string) => void
  onHelpActionClick?: (intent: string, orderId: string) => void
  onSaveSlotClick?: (orderId: string, selection: SaveSlotSelection) => void
  onSkipDeliveryClick?: (orderId: string) => void
  onRecipeActionClick?: (action: RecipeAction) => void
}

const STATE_COLOR_MAP: Record<OrderStateType, Color> = {
  positive: Color.Success_800,
  neutral: Color.ColdGrey_800,
  warning: Color.Warning_900,
  negative: Color.Error_900,
}

const LinkWithoutUnderline = withCitrusTheme(Link, () => ({
  textDecoration: 'none',
  boxShadow: 'none',
}))

const StyledEmptyPlate = withCitrusTheme(Image, () => ({
  filter: 'drop-shadow(10px 17px 11px #00000021)',
  boxShadow:
    '1px 2px 5px 0px #0000001A, 4px 8px 9px 0px #00000017, 10px 17px 12px 0px #0000000D, 18px 31px 14px 0px #00000003, 28px 48px 16px 0px #00000000',
  borderRadius: '50%',
}))

const EmptyRecipeImage = () => (
  <AspectRatio ratio={1 / 1}>
    <Box
      borderStyle={BorderStyle.Dashed}
      borderColor={Color.ColdGrey_200}
      borderWidth={0.5}
      width="100%"
      data-testid="order-card-recipe-placeholder"
      paddingBottom={[1, 3]}
      paddingH={[1, 3]}
      paddingRight={[1, 3]}
      paddingV={[1, 3]}
    >
      <StyledEmptyPlate src={EmptyPlateJPG} alt="Add Recipe" />
    </Box>
  </AspectRatio>
)

type RecipeImageProps = { src: string; alt: string }

const RecipeImage = ({ src, alt }: RecipeImageProps) => (
  <AspectRatio ratio={1 / 1}>
    <Image src={src} alt={alt} />
  </AspectRatio>
)

const MAX_RECIPE_SLOTS = 5

// A div, not a Link: an inline anchor collapses AspectRatio's width and blanks the image.
const ClickableRecipe = withCitrusTheme(Box, () => ({
  cursor: 'pointer',
}))

type RecipeStripProps = {
  recipes: OrderRecipe[]
  onRecipeActionClick?: (action: RecipeAction) => void
  // The order-scoped action an empty plate reuses, present only on editable orders. An
  // empty plate fires it through `onRecipeActionClick` so it behaves exactly like a filled
  // tile (same plates_click analytics and menu route), mirroring the legacy experience.
  emptyPlateAction?: RecipeAction
}

const RecipeStrip = ({ recipes, onRecipeActionClick, emptyPlateAction }: RecipeStripProps) => {
  const slots = Array.from({ length: MAX_RECIPE_SLOTS }, (_, index) => recipes[index] ?? null)

  return (
    <Box
      width="100%"
      display={GridDisplay.Grid}
      gridTemplateColumns={`repeat(${MAX_RECIPE_SLOTS}, 1fr)`}
      gap={[2, 4]}
      data-testid="order-card-recipe-strip"
    >
      {slots.map((recipe, index) => {
        if (!recipe) {
          if (!emptyPlateAction) {
            return (
              <Box key={`placeholder-${index}`} width="100%">
                <EmptyRecipeImage />
              </Box>
            )
          }

          const handleActivate = () => onRecipeActionClick?.(emptyPlateAction)

          return (
            <Box key={`placeholder-${index}`} width="100%">
              <ClickableRecipe
                width="100%"
                role="button"
                tabIndex={0}
                onClick={handleActivate}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    handleActivate()
                  }
                }}
                data-testid={`order-card-recipe-placeholder-action-${index}`}
              >
                <EmptyRecipeImage />
              </ClickableRecipe>
            </Box>
          )
        }

        const { action } = recipe
        const image = <RecipeImage src={recipe.image_url} alt={recipe.accessibility_label} />

        if (!action) {
          return (
            <Box key={recipe.id} width="100%" data-testid={`order-card-recipe-${recipe.id}`}>
              {image}
            </Box>
          )
        }

        const handleActivate = () => onRecipeActionClick?.(action)

        return (
          <Box key={recipe.id} width="100%" data-testid={`order-card-recipe-${recipe.id}`}>
            <ClickableRecipe
              width="100%"
              role="button"
              tabIndex={0}
              onClick={handleActivate}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  handleActivate()
                }
              }}
              data-testid={`order-card-recipe-action-${recipe.id}`}
            >
              {image}
            </ClickableRecipe>
          </Box>
        )
      })}
    </Box>
  )
}

type EditRecipesIconLinkProps = {
  action: DashboardActionRef
  onClick: () => void
  testId: string
}

const EditRecipesIconLink = ({ action, onClick, testId }: EditRecipesIconLinkProps) => (
  <LinkWithoutUnderline onClick={onClick} data-testid={testId}>
    <Box display={Display.Flex} alignItems={AlignItems.Center}>
      <Icon size={4} name="edit" variant={IconVariant.Actionable} />
      <Text size={4} fontWeight={700} color={Color.Secondary_400}>
        {action.title}
      </Text>
    </Box>
  </LinkWithoutUnderline>
)

type OrderCardPriceProps = {
  price: OrderPrice
  testId: string
}

const OrderCardPriceBlock = ({ price, testId }: OrderCardPriceProps) => {
  const hasOriginal = Boolean(price.original) && price.original !== price.amount

  if (!hasOriginal) {
    return (
      <Text size={7} display={Display.Inline} data-testid={testId}>
        <Bold>{price.amount}</Bold>
      </Text>
    )
  }

  return (
    <Text data-testid={testId}>
      <Text size={[2, 6]} display={Display.Inline} color={Color.ColdGrey_600}>
        <s>{price.original}</s>
      </Text>
      <br />
      <Text size={[3, 7]} display={Display.Inline}>
        <Bold>{price.amount}</Bold>
      </Text>
    </Text>
  )
}

export const OrderCard = ({
  data,
  hasSaveSlotError,
  hasSkipDeliveryError,
  isSaveSlotPending,
  isSkipDeliveryPending,
  onPrimaryActionClick,
  onHelpActionClick,
  onSaveSlotClick,
  onSkipDeliveryClick,
  onRecipeActionClick,
}: OrderCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false)

  // Fall back to the neutral colour for any unrecognised state_type the xAPI may send.
  const stateColor = STATE_COLOR_MAP[data.state_type] ?? STATE_COLOR_MAP.neutral
  const canExpand = Boolean(data.details)

  // An empty plate reuses sibling action to behave like a filled tile.
  const emptyPlateAction = data.recipes.find(
    (recipe) => recipe.action && !recipe.action.recipe_url,
  )?.action

  const handleExpandToggle = () => {
    if (!canExpand) return
    setIsExpanded((prev) => !prev)
  }

  const handlePrimaryActionClick = () => {
    if (!data.primary_action) return
    onPrimaryActionClick?.(data.primary_action.intent, data.id)
  }

  const handleHelpActionClick = () => {
    if (!data.help_cta) return
    onHelpActionClick?.(data.help_cta.intent, data.id)
  }

  return (
    <Box width="100%" display={Display.Flex} flexDirection={FlexDirection.Column}>
      {data.heading ? (
        <>
          <Body1
            color={Color.ColdGrey_900}
            fontWeight={FontWeight.Bold}
            data-testid={`order-card-heading-${data.id}`}
          >
            {data.heading}
          </Body1>
          <Space size={4} direction="vertical" />
        </>
      ) : null}
      <Box
        width="100%"
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        borderWidth={0.5}
        borderColor={Color.ColdGrey_100}
        borderStyle={BorderStyle.Solid}
        paddingV={[3, 3, 8]}
        paddingH={[3, 3, 8]}
        bg={Color.White}
        data-testid={`order-card-${data.id}`}
      >
        <Grid width="100%" gap={[0, 0, 10]}>
          {/* Col 1 — date / state / summary (+ mobile right rail) + below-row copy */}
          <Col size={[12, 12, 3]}>
            <Box display={Display.Flex} flexDirection={FlexDirection.Column} height="100%">
              <Join with={<Space size={[4, 6]} direction="vertical" />}>
                <Box
                  display={Display.Flex}
                  flexDirection={FlexDirection.Row}
                  justifyContent={JustifyContent.SpaceBetween}
                  height="100%"
                >
                  <Box
                    height="100%"
                    display={Display.Flex}
                    flexDirection={FlexDirection.Column}
                    justifyContent={JustifyContent.SpaceBetween}
                    width="100%"
                  >
                    <Box
                      height="100%"
                      display={Display.Flex}
                      justifyContent={JustifyContent.SpaceBetween}
                    >
                      <Box>
                        <Heading5>{data.title}</Heading5>
                        <Body2 color={stateColor} fontWeight={FontWeight.Bold}>
                          {data.state_title}
                        </Body2>
                        <Body3 color={Color.ColdGrey_800} size={[1, 2]}>
                          {data.summary_text}
                        </Body3>
                        <Space size={3} direction="vertical" />
                      </Box>
                      {/* Mobile + tablet right rail: price sits to the left of the chevron,
                          top-aligned with the title. */}
                      <Box
                        display={[Display.Flex, Display.Flex, Display.None]}
                        flexDirection={FlexDirection.Row}
                        alignItems={AlignItems.FlexStart}
                        gap={2}
                      >
                        <OrderCardPriceBlock price={data.price} testId="order-card-price-mobile" />
                        {canExpand ? (
                          <ExpandToggle isExpanded={isExpanded} onToggle={handleExpandToggle} />
                        ) : null}
                      </Box>
                    </Box>
                    {data.edit_until_label ? (
                      <>
                        <Body3 color={Color.ColdGrey_600} size={2}>
                          {data.edit_until_label}
                        </Body3>
                        <Space size={[1, 0]} direction="vertical" />
                      </>
                    ) : null}
                    {/* Tablet/desktop: help button lives in the left column under the summary. */}
                    {data.help_cta ? (
                      <Box display={[Display.None, Display.Block]}>
                        <Space size={4} direction="vertical" />
                        <Button
                          colorVariant={ButtonColorVariant.Secondary}
                          width="100%"
                          onClick={handleHelpActionClick}
                          data-testid={`order-card-help-action-desktop-${data.help_cta.intent}`}
                        >
                          {data.help_cta.title}
                        </Button>
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              </Join>
            </Box>
          </Col>

          {/* Col 2 — recipe images + mobile-only edit link */}
          <Col size={[12, 12, 7]}>
            <RecipeStrip
              recipes={data.recipes}
              onRecipeActionClick={onRecipeActionClick}
              emptyPlateAction={emptyPlateAction}
            />
            <Space size={[6, 6, 0]} direction="vertical" />
            {data.primary_action ? (
              <LinkWithoutUnderline
                onClick={handlePrimaryActionClick}
                data-testid={`order-card-primary-action-mobile-${data.primary_action.intent}`}
              >
                <Box
                  display={[Display.Flex, Display.Flex, Display.None]}
                  alignItems={AlignItems.Center}
                >
                  <Icon size={4} name="edit" variant={IconVariant.Actionable} />
                  <Text size={4} fontWeight={700} color={Color.Secondary_400}>
                    {data.primary_action.title}
                  </Text>
                </Box>
              </LinkWithoutUnderline>
            ) : null}
            {/* Mobile: help button sits full-width below the recipe strip. The gap above
                comes from the recipe-strip spacer (Space size={[6, 6, 0]}). */}
            {data.help_cta ? (
              <Box display={[Display.Block, Display.None]} width="100%">
                <Button
                  colorVariant={ButtonColorVariant.Secondary}
                  width="100%"
                  onClick={handleHelpActionClick}
                  data-testid={`order-card-help-action-mobile-${data.help_cta.intent}`}
                >
                  {data.help_cta.title}
                </Button>
              </Box>
            ) : null}
          </Col>

          {/* Col 3 — desktop-only action stack: edit link + price/chevron row */}
          <Col size={2}>
            <Box
              display={[Display.None, Display.None, Display.Flex]}
              flexDirection={FlexDirection.Column}
              justifyContent={JustifyContent.SpaceBetween}
              alignItems={AlignItems.FlexEnd}
              height="100%"
            >
              <Join with={<Space size={6} direction="horizontal" />}>
                {data.primary_action ? (
                  <EditRecipesIconLink
                    action={data.primary_action}
                    onClick={handlePrimaryActionClick}
                    testId={`order-card-primary-action-desktop-${data.primary_action.intent}`}
                  />
                ) : null}
                <Box display={Display.Flex}>
                  <Box paddingRight={4}>
                    <OrderCardPriceBlock price={data.price} testId="order-card-price-desktop" />
                  </Box>
                  {canExpand ? (
                    <ExpandToggle isExpanded={isExpanded} onToggle={handleExpandToggle} />
                  ) : null}
                </Box>
              </Join>
              <div />
            </Box>
          </Col>
        </Grid>

        {isExpanded && data.details ? (
          // Remount to re-seed selection when the server's booked day/slot changes.
          <OrderCardPanel
            key={`${data.details.selected_day_id ?? ''}-${data.details.selected_slot_id ?? ''}`}
            orderId={data.id}
            details={data.details}
            hasSaveSlotError={hasSaveSlotError}
            hasSkipDeliveryError={hasSkipDeliveryError}
            isSaveSlotPending={isSaveSlotPending}
            isSkipDeliveryPending={isSkipDeliveryPending}
            onSaveSlotClick={onSaveSlotClick}
            onSkipDeliveryClick={onSkipDeliveryClick}
          />
        ) : null}
      </Box>
    </Box>
  )
}
